import '@fontsource/roboto-condensed'
import '@fontsource/bebas-neue'
import '@fontsource/inter'

import * as React from 'react'

import { Global, css } from '@emotion/react'
import Box from '@mui/material/Box'
import { ThemeProvider } from '@mui/material/styles'
import { find } from 'lodash'
// import RootPage from 'pages/RootPage'
import { match, MatchResult, pathToRegexp } from 'path-to-regexp'

import { useSyncClientSize } from 'domains/application/client-size'
import { ErrorPage } from 'domains/application/ErrorPage'
import { useSelector } from 'domains/application/hooks'
import 'lib/fonts.css'
import { theme } from 'domains/application/theme'

// TODO: replace keys usage with
// https://github.com/pillarjs/path-to-regexp?tab=readme-ov-file#match
type RouteMatch = [
  (path: string) => MatchResult<Record<string, string>> | false,
  regexp: RegExp,
]
type Route = [...RouteMatch, React.JSX.Element]

const makeRoute = (path: string) => {
  const { regexp } = pathToRegexp(path)
  const fn = match(path)
  return [fn, regexp] as RouteMatch
}

const HomePage = React.lazy(() => import('pages/RootPage'))
const QuickstartPage = React.lazy(() => import('pages/QuickstartPage'))
const PreviewPage = React.lazy(() => import('pages/Preview'))

const routes: Route[] = [
  [...makeRoute('{/:locale}/'), <HomePage key='home' />],
  [...makeRoute('{/:locale}/quickstart'), <QuickstartPage key='home' />],
  [...makeRoute('{/:locale}/preview/:key'), <PreviewPage key='preview' />],
  [...makeRoute('{/:locale}/*unknown'), <ErrorPage key='ErrorPage' />],
]

export const getRoute = function getRoute(
  path: string,
): (typeof routes)[number] {
  const route = find(routes, ([matchFunction, _regexp, _comp]) => {
    return matchFunction(path)
  })

  return route as (typeof routes)[0]
}

export const Application = function Application({
  className,
}: {
  className?: string
}) {
  const path = useSelector((state) => state.application.path)
  const [_matchFunction, _regexp, Component] = React.useMemo(
    () => getRoute(path),
    [path],
  ) as Route
  const DeferredComponent = React.useDeferredValue(Component)

  const WithThemeContext = React.useMemo(() => {
    return () => {
      useSyncClientSize()
      return null
    }
  }, [])

  return (
    <>
      <Global
        styles={css`
          body {
            font-family: 'Bebas Neue', sans-serif;
            font-weight: 400;
            font-style: normal;
            background-color: #f8fff8;
          }

          code {
            font-family:
              Monaco, Menlo, 'Ubuntu Mono', Consolas, 'Source Code Pro',
              source-code-pro, monospace;
            font-size: 12px;
          }
        `}
      />
      <ThemeProvider theme={theme}>
        <WithThemeContext />
        <Box className={className} color='text.primary' typography='body1'>
          <React.Suspense>{DeferredComponent}</React.Suspense>
        </Box>
      </ThemeProvider>
    </>
  )
}

export default Application
